import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { t } from '../../../../types/translation/Translator';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { ProductIcon } from '../../../icons/Product/ProductIcon';
import { ProductMasterDataFilterDropdownContent, ProductMasterDataFilterInnerContent } from './DropdownContent';
import { CustomFieldFilter } from '../CustomFieldFilter';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { TagFilter } from '../TagFilter';
import { TagRelation } from '../../../../types/tag';
import { EntityType } from '../../../../types/comment';
import { productMasterDataTableFilter } from '../../../../ui/components/Table/ProductMasterData/ProductMasterDataTable';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { ProductMasterData } from '../../../../types/product_master_data';

export class ProductMasterDataFilter extends BaseFilter<ProductMasterData> {
  customFields: Map<string, CustomField>;
  tagRelations: Map<string, TagRelation>;
  company: Company;
  tableSettings?: TableSettings<any>;
  filter?: (item: ProductMasterData) => boolean;

  constructor(
    customFields: Map<string, CustomField>,
    tagRelations: Map<string, TagRelation>,
    company: Company,
    tableSettings?: TableSettings<any>,
    filter?: (item: ProductMasterData) => boolean,
  ) {
    super(FilterStorageKey.PRODUCT_MASTER_DATA);
    this.customFields = customFields;
    this.tagRelations = tagRelations;
    this.company = company;
    this.tableSettings = tableSettings;
    this.filter = filter;
  }

  isSelectable(item: ProductMasterData): boolean {
    return this.filter ? this.filter(item) : true;
  }

  filterOptions(): BaseFilter<any>[] {
    return [...(this.company.partner ? [new CompanyPartnerFilter()] : []), new TagFilter(EntityType.pmd)];
  }

  toLabel(): string {
    return t(TranslationKey.productMasterData).singular.label;
  }

  toCount(props: BaseFilterProps): number {
    return props.filterProps.product?.size || 0;
  }

  toIcon(): JSX.Element {
    return <ProductIcon className={this.className} />;
  }

  toDropdownContent(props: BaseFilterProps): JSX.Element {
    return (
      <ProductMasterDataFilterDropdownContent
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
      />
    );
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    return (
      <ProductMasterDataFilterInnerContent
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
      />
    );
  }

  getTableSettings(): TableSettings<any> | undefined {
    return this.tableSettings;
  }

  search(item: ProductMasterData, query: string) {
    return productMasterDataTableFilter(item, query, this.getTableSettings());
  }

  filteredItems(items: ProductMasterData[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      return [
        TagFilter.filterBy(filterProps, EntityType.pmd, this.tagRelations, item.id),
        ProductMasterDataFilter.filter(filterProps, item, this.customFields, this.tagRelations),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(
    filterProps: FilterProps,
    item: ProductMasterData | undefined,
    customFields: Map<string, CustomField>,
    tagRelations: Map<string, TagRelation>,
  ) {
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    if (item && filterProps.serialManaged && !item.serialManaged) return false;
    if (item && filterProps.lotManaged && !item.lotManaged) return false;
    if (item && filterProps.product?.size && !filterProps.product.has(item.id)) return false;

    return CustomFieldFilter.filter(filterProps, item, customFields, CustomFieldEntityType.productMasterData);
  }
}
